import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { Modal, ModalField, ModalTextarea, ModalSelect } from "@/pages/Channels";
import {
  Broadcast, Users, Plus, ArrowLeft, Copy, CheckCircle, Prohibit, PencilSimple,
  Play, Pause, ClipboardText, ListMagnifyingGlass, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { key: "clients", label: "Clients", icon: Users },
  { key: "distributions", label: "Distributions", icon: Broadcast },
  { key: "logs", label: "Access Logs", icon: ListMagnifyingGlass },
];

// -------- state visuals --------
const STATE_CLS = {
  ACTIVE: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10",
  INACTIVE: "text-red-500 border-red-500/30 bg-red-500/10",
  DRAFT: "text-neutral-400 border-neutral-500/30 bg-neutral-500/10",
  DISABLED: "text-amber-500 border-amber-500/30 bg-amber-500/10",
  REVOKED: "text-red-500 border-red-500/30 bg-red-500/10",
  EXPIRED: "text-orange-500 border-orange-500/30 bg-orange-500/10",
};
function StateBadge({ value }) {
  return (
    <span data-testid={`state-badge-${value}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-2 py-0.5 border ${STATE_CLS[value] || STATE_CLS.DRAFT}`}>
      {value}
    </span>
  );
}
function isExpired(d) {
  return d.expires_at && new Date(d.expires_at).getTime() <= Date.now();
}
function effectiveState(d) {
  if (d.status === "ACTIVE" && isExpired(d)) return "EXPIRED";
  return d.status;
}
function toIso(localValue) {
  // datetime-local -> tz-aware ISO so the backend's fromisoformat comparison is safe
  if (!localValue) return null;
  return new Date(localValue).toISOString().replace("Z", "+00:00");
}

export default function Syndication() {
  const [tab, setTab] = useState("clients");
  return (
    <>
      <PageHeader title="Syndication" subtitle="Distribute CCS channels to external clients" />
      <div className="px-8 pt-6 ccs-enter" data-testid="syndication-page">
        <div className="flex gap-1 border-b border-[#262626] mb-6">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`synd-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 text-sm px-4 py-2.5 border-b-2 -mb-px transition-colors duration-150 ${tab === t.key ? "border-[#3A86FF] text-white" : "border-transparent text-[#737373] hover:text-white"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-8 pb-10">
        {tab === "clients" && <ClientsTab />}
        {tab === "distributions" && <DistributionsTab />}
        {tab === "logs" && <LogsTab />}
      </div>
    </>
  );
}

/* ============================ CLIENTS ============================ */
function ClientsTab() {
  const [clients, setClients] = useState(null);
  const [modal, setModal] = useState(null); // {type:'create'|'edit', client}
  const load = useCallback(() => api.get("/platform/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex justify-end mb-4">
        <button data-testid="create-client-btn" onClick={() => setModal({ type: "create" })}
          className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">
          <Plus size={16} weight="bold" /> New Client
        </button>
      </div>
      {!clients ? <Loading /> : clients.length === 0 ? (
        <EmptyState icon={Users} msg="No clients yet. Create a client to start syndicating channels." testid="clients-empty" />
      ) : (
        <div className="border border-[#262626] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[#737373] text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-bold px-4 py-3">Client</th>
                <th className="text-left font-bold px-4 py-3">Status</th>
                <th className="text-left font-bold px-4 py-3">Contact</th>
                <th className="text-left font-bold px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-[#262626] hover:bg-[#141414] transition-colors duration-150">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3"><StateBadge value={c.status} /></td>
                  <td className="px-4 py-3 text-xs text-[#A3A3A3]">{c.contact_email || c.contact_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#737373]">{(c.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`manage-client-${c.id}`} onClick={() => setModal({ type: "edit", client: c })}
                      className="text-xs text-[#3A86FF] hover:underline">Manage</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && <ClientModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </>
  );
}

function ClientModal({ modal, onClose, onDone }) {
  const edit = modal.type === "edit";
  const c = modal.client || {};
  const [f, setF] = useState({
    name: c.name || "", contact_name: c.contact_name || "", contact_email: c.contact_email || "",
    contact_phone: c.contact_phone || "", notes: c.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const [confirmDeact, setConfirmDeact] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) { toast.error("Client name is required"); return; }
    setLoading(true);
    try {
      const body = { name: f.name, contact_name: f.contact_name || null, contact_email: f.contact_email || null, contact_phone: f.contact_phone || null, notes: f.notes || null };
      if (edit) { await api.patch(`/platform/clients/${c.id}`, body); toast.success("Client updated"); }
      else { await api.post("/platform/clients", body); toast.success("Client created"); }
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Failed"); setLoading(false); }
  };

  const setStatus = async (status) => {
    setLoading(true);
    try {
      await api.patch(`/platform/clients/${c.id}`, { status });
      toast.success(status === "ACTIVE" ? "Client activated" : "Client deactivated");
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setLoading(false); }
  };

  const del = async () => {
    setLoading(true);
    try {
      await api.delete(`/platform/clients/${c.id}`);
      toast.success("Client deleted");
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setLoading(false); }
  };

  if (confirmDelete) {
    return (
      <Modal title="Delete client?" onClose={() => setConfirmDelete(false)}>
        <div className="space-y-4" data-testid="confirm-delete-client">
          <p className="text-sm text-[#A3A3A3]">Delete <span className="text-white font-medium">{c.name}</span>? It will be removed from this list. This is allowed only for a deactivated client with no active distributions.</p>
          <p className="text-xs text-amber-500/90 bg-amber-500/10 border border-amber-500/20 rounded-sm px-3 py-2">The client's distribution records and access logs are preserved for audit. If any distribution is still active, delete will be blocked until you disable or revoke it.</p>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setConfirmDelete(false)} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
            <button data-testid="confirm-delete-client-btn" disabled={loading} onClick={del}
              className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60">Delete</button>
          </div>
        </div>
      </Modal>
    );
  }

  if (confirmDeact) {
    return (
      <Modal title="Deactivate client?" onClose={() => setConfirmDeact(false)}>
        <div className="space-y-4" data-testid="confirm-deactivate-client">
          <p className="text-sm text-[#A3A3A3]">Deactivating <span className="text-white font-medium">{c.name}</span> immediately blocks <span className="text-white">new</span> distribution manifest requests for all of its distributions.</p>
          <p className="text-xs text-amber-500/90 bg-amber-500/10 border border-amber-500/20 rounded-sm px-3 py-2">Note: media URLs that were already handed out stay playable until their signed token expires (typically ~15–20 minutes). Deactivation does not revoke already-issued segment URLs.</p>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setConfirmDeact(false)} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
            <button data-testid="confirm-deactivate-client-btn" disabled={loading} onClick={() => setStatus("INACTIVE")}
              className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60">Deactivate</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={edit ? "Manage Client" : "New Client"} onClose={onClose} size="lg">
      <div className="space-y-4" data-testid="client-modal">
        {edit && (
          <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-3">
            <div className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-wider text-[#737373]">Status</span><StateBadge value={c.status} /></div>
            {c.status === "ACTIVE" ? (
              <button data-testid="client-deactivate-btn" onClick={() => setConfirmDeact(true)}
                className="flex items-center gap-2 text-xs text-red-500 border border-red-500/30 hover:bg-red-500/10 rounded-sm px-3 py-1.5 transition-colors duration-150"><Prohibit size={14} /> Deactivate</button>
            ) : (
              <div className="flex items-center gap-2">
                <button data-testid="client-activate-btn" disabled={loading} onClick={() => setStatus("ACTIVE")}
                  className="flex items-center gap-2 text-xs text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/10 rounded-sm px-3 py-1.5 transition-colors duration-150"><CheckCircle size={14} /> Activate</button>
                <button data-testid="client-delete-btn" onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-2 text-xs text-[#A3A3A3] border border-[#404040] hover:bg-white/5 rounded-sm px-3 py-1.5 transition-colors duration-150"><Trash size={14} /> Delete</button>
              </div>
            )}
          </div>
        )}
        <ModalField label="Client name" testid="client-name-input" value={f.name} onChange={set("name")} placeholder="e.g. Acme Streaming" />
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Contact name" testid="client-contact-name-input" value={f.contact_name} onChange={set("contact_name")} />
          <ModalField label="Contact email" testid="client-contact-email-input" type="email" value={f.contact_email} onChange={set("contact_email")} />
        </div>
        <ModalField label="Contact phone" testid="client-contact-phone-input" value={f.contact_phone} onChange={set("contact_phone")} />
        <ModalTextarea label="Notes" testid="client-notes-input" value={f.notes} onChange={set("notes")} rows={3} />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
          <button data-testid="submit-client-modal" disabled={loading} onClick={save}
            className="bg-[#3A86FF] hover:bg-[#2563EB] text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60 transition-colors duration-150">
            {loading ? "Saving…" : edit ? "Save" : "Create Client"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ========================= DISTRIBUTIONS ========================= */
function DistributionsTab() {
  const [dists, setDists] = useState(null);
  const [clients, setClients] = useState([]);
  const [channels, setChannels] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null); // {dist, action}

  const load = useCallback(() => {
    const q = filterClient ? `?client_id=${filterClient}` : "";
    api.get(`/platform/distributions${q}`).then((r) => setDists(r.data)).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, [filterClient]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/platform/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/channels").then((r) => setChannels(r.data)).catch(() => {});
  }, []);

  const transition = async (dist, status) => {
    try {
      await api.patch(`/platform/distributions/${dist.id}`, { status });
      toast.success(`Distribution ${status.toLowerCase()}`);
      setConfirm(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const del = async (dist) => {
    try {
      await api.delete(`/platform/distributions/${dist.id}`);
      toast.success("Distribution deleted");
      setConfirm(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const shown = (dists || []).filter((d) => !filterStatus || effectiveState(d) === filterStatus);

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex gap-2">
          <select data-testid="dist-filter-client" value={filterClient} onChange={(e) => setFilterClient(e.target.value)}
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none text-[#A3A3A3]">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="dist-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none text-[#A3A3A3]">
            <option value="">All states</option>
            {["DRAFT", "ACTIVE", "DISABLED", "REVOKED", "EXPIRED"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button data-testid="create-distribution-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] text-white text-sm font-medium px-4 py-2 rounded-sm transition-colors duration-150">
          <Plus size={16} weight="bold" /> New Distribution
        </button>
      </div>

      {!dists ? <Loading /> : shown.length === 0 ? (
        <EmptyState icon={Broadcast} msg="No distributions match. Create one to give a client a channel feed." testid="distributions-empty" />
      ) : (
        <div className="space-y-3">
          {shown.map((d) => (
            <div key={d.id} data-testid={`distribution-row-${d.id}`} className="bg-[#141414] border border-[#262626] rounded-sm p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StateBadge value={effectiveState(d)} />
                    <span className="text-sm font-medium">{d.channel_name || d.channel_id}</span>
                    <span className="text-xs text-[#737373]">→ {d.client_name || d.client_id}</span>
                  </div>
                  <DistUrl publicId={d.public_id} testid={`dist-url-${d.id}`} />
                  <div className="text-[11px] text-[#525252] mt-1 font-mono">
                    req {d.request_count ?? 0}{d.last_request_at ? ` · last ${d.last_request_at.slice(0, 19).replace("T", " ")}` : ""}{d.expires_at ? ` · expires ${d.expires_at.slice(0, 10)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {d.status !== "ACTIVE" && d.status !== "REVOKED" && (
                    <button data-testid={`dist-activate-${d.id}`} onClick={() => transition(d, "ACTIVE")}
                      className="flex items-center gap-1.5 text-xs text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/10 rounded-sm px-3 py-1.5 transition-colors duration-150"><Play size={13} /> Activate</button>
                  )}
                  {d.status === "ACTIVE" && (
                    <button data-testid={`dist-disable-${d.id}`} onClick={() => transition(d, "DISABLED")}
                      className="flex items-center gap-1.5 text-xs text-amber-500 border border-amber-500/30 hover:bg-amber-500/10 rounded-sm px-3 py-1.5 transition-colors duration-150"><Pause size={13} /> Disable</button>
                  )}
                  {d.status !== "REVOKED" && (
                    <button data-testid={`dist-revoke-${d.id}`} onClick={() => setConfirm({ dist: d, action: "REVOKED" })}
                      className="flex items-center gap-1.5 text-xs text-red-500 border border-red-500/30 hover:bg-red-500/10 rounded-sm px-3 py-1.5 transition-colors duration-150"><Prohibit size={13} /> Revoke</button>
                  )}
                  {d.status !== "ACTIVE" && (
                    <button data-testid={`dist-delete-${d.id}`} onClick={() => setConfirm({ dist: d, action: "DELETE" })}
                      className="flex items-center gap-1.5 text-xs text-[#A3A3A3] border border-[#404040] hover:bg-white/5 rounded-sm px-3 py-1.5 transition-colors duration-150"><Trash size={13} /> Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateDistributionModal clients={clients} channels={channels} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
      {confirm && confirm.action === "DELETE" && (
        <Modal title="Delete distribution?" onClose={() => setConfirm(null)}>
          <div className="space-y-4" data-testid="confirm-delete-dist">
            <p className="text-sm text-[#A3A3A3]">Delete the distribution of <span className="text-white font-medium">{confirm.dist.channel_name || confirm.dist.channel_id}</span> to <span className="text-white font-medium">{confirm.dist.client_name || confirm.dist.client_id}</span>? It will be removed from this list.</p>
            <p className="text-xs text-amber-500/90 bg-amber-500/10 border border-amber-500/20 rounded-sm px-3 py-2">Deletion blocks all future manifest access (the public URL keeps returning 404), but it cannot immediately invalidate Bunny segment URLs already handed out — those lapse when their signed token expires (~15–20 minutes). Access logs and audit history are preserved.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setConfirm(null)} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
              <button data-testid="confirm-delete-dist-btn" onClick={() => del(confirm.dist)}
                className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-sm">Delete</button>
            </div>
          </div>
        </Modal>
      )}
      {confirm && confirm.action === "REVOKED" && (
        <Modal title="Revoke distribution?" onClose={() => setConfirm(null)}>
          <div className="space-y-4" data-testid="confirm-revoke-dist">
            <p className="text-sm text-[#A3A3A3]">Revoking permanently disables this distribution for <span className="text-white font-medium">{confirm.dist.client_name}</span>. The record is kept for audit and cannot be reactivated.</p>
            <p className="text-xs text-amber-500/90 bg-amber-500/10 border border-amber-500/20 rounded-sm px-3 py-2">New manifest requests are blocked immediately, but media URLs already handed out remain playable until their signed token expires (~15–20 minutes).</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setConfirm(null)} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
              <button data-testid="confirm-revoke-dist-btn" onClick={() => transition(confirm.dist, "REVOKED")}
                className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-sm">Revoke</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function DistUrl({ publicId, testid }) {
  const url = `${window.location.origin}/api/d/${publicId}/playlist.m3u8`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Distribution URL copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select the URL manually"); }
  };
  return (
    <div className="flex items-center gap-2">
      <code data-testid={testid} className="text-[11px] text-[#A3A3A3] bg-[#0A0A0A] border border-[#262626] rounded-sm px-2 py-1 truncate max-w-[520px]">{url}</code>
      <button data-testid={`${testid}-copy`} onClick={copy} className="text-[#737373] hover:text-[#3A86FF] transition-colors duration-150" title="Copy URL">
        {copied ? <CheckCircle size={15} className="text-emerald-500" /> : <Copy size={15} />}
      </button>
    </div>
  );
}

function CreateDistributionModal({ clients, channels, onClose, onDone }) {
  const [f, setF] = useState({ client_id: "", channel_id: "", status: "DRAFT", expires_at: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const activeClients = clients.filter((c) => c.status === "ACTIVE");

  const submit = async () => {
    if (!f.client_id || !f.channel_id) { toast.error("Client and channel are required"); return; }
    setLoading(true);
    try {
      await api.post("/platform/distributions", {
        client_id: f.client_id, channel_id: f.channel_id, status: f.status,
        expires_at: toIso(f.expires_at),
      });
      toast.success("Distribution created");
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Failed"); setLoading(false); }
  };

  return (
    <Modal title="New Distribution" onClose={onClose} size="lg">
      <div className="space-y-4" data-testid="create-distribution-modal">
        <ModalSelect label="Client" testid="dist-client-select" value={f.client_id} onChange={set("client_id")}>
          <option value="">Select a client…</option>
          {activeClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </ModalSelect>
        {clients.length > 0 && activeClients.length === 0 && (
          <p className="text-xs text-amber-500">No ACTIVE clients — activate a client first.</p>
        )}
        <ModalSelect label="Channel" testid="dist-channel-select" value={f.channel_id} onChange={set("channel_id")}>
          <option value="">Select a channel…</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </ModalSelect>
        <div className="grid grid-cols-2 gap-3">
          <ModalSelect label="Initial status" testid="dist-status-select" value={f.status} onChange={set("status")}>
            <option value="DRAFT">Draft</option>
            <option value="ACTIVE">Active</option>
          </ModalSelect>
          <ModalField label="Expires (optional)" testid="dist-expires-input" type="datetime-local" value={f.expires_at} onChange={set("expires_at")} />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
          <button data-testid="submit-distribution-modal" disabled={loading} onClick={submit}
            className="bg-[#3A86FF] hover:bg-[#2563EB] text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60 transition-colors duration-150">
            {loading ? "Creating…" : "Create Distribution"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* =========================== ACCESS LOGS =========================== */
const RESULT_CLS = { success: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10", denied: "text-red-500 border-red-500/30 bg-red-500/10" };
function LogsTab() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ distribution_id: "", client_id: "", channel_id: "", result: "" });
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const set = (k) => (e) => { setFilters({ ...filters, [k]: e.target.value }); setOffset(0); };

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.set("limit", limit); p.set("offset", offset);
    api.get(`/platform/distribution-access-logs?${p.toString()}`).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, [filters, limit, offset]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex items-end gap-2 mb-4 flex-wrap" data-testid="logs-filters">
        <FilterInput label="Distribution ID" testid="log-filter-distribution" value={filters.distribution_id} onChange={set("distribution_id")} />
        <FilterInput label="Client ID" testid="log-filter-client" value={filters.client_id} onChange={set("client_id")} />
        <FilterInput label="Channel ID" testid="log-filter-channel" value={filters.channel_id} onChange={set("channel_id")} />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Result</div>
          <select data-testid="log-filter-result" value={filters.result} onChange={set("result")}
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none text-[#A3A3A3]">
            <option value="">All</option><option value="success">Success</option><option value="denied">Denied</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Per page</div>
          <select data-testid="log-page-size" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none text-[#A3A3A3]">
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? (
        <EmptyState icon={ClipboardText} msg="No access log entries match these filters." testid="logs-empty" />
      ) : (
        <>
          <div className="border border-[#262626] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-[#737373] text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left font-bold px-3 py-3">Timestamp</th>
                  <th className="text-left font-bold px-3 py-3">Result</th>
                  <th className="text-left font-bold px-3 py-3">HTTP</th>
                  <th className="text-left font-bold px-3 py-3">Latency</th>
                  <th className="text-left font-bold px-3 py-3">Error</th>
                  <th className="text-left font-bold px-3 py-3">Distribution</th>
                  <th className="text-left font-bold px-3 py-3">Client</th>
                  <th className="text-left font-bold px-3 py-3">Channel</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {data.items.map((l, i) => (
                  <tr key={i} data-testid={`log-row-${i}`} className="border-t border-[#262626] hover:bg-[#141414]">
                    <td className="px-3 py-2 whitespace-nowrap">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider rounded px-2 py-0.5 border ${RESULT_CLS[l.result] || ""}`}>{l.result}</span></td>
                    <td className="px-3 py-2">{l.status_code}</td>
                    <td className="px-3 py-2">{l.latency_ms != null ? `${l.latency_ms}ms` : "—"}</td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{l.error_category || "—"}</td>
                    <td className="px-3 py-2 text-[#737373]">{l.distribution_id || "—"}</td>
                    <td className="px-3 py-2 text-[#737373]">{l.client_id || "—"}</td>
                    <td className="px-3 py-2 text-[#737373]">{l.channel_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-[#737373]">
            <span data-testid="logs-range">{offset + 1}–{offset + data.items.length} of {data.total}</span>
            <div className="flex gap-2">
              <button data-testid="logs-prev" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
                className="border border-[#262626] rounded-sm px-3 py-1.5 hover:bg-[#141414] disabled:opacity-40">Prev</button>
              <button data-testid="logs-next" disabled={offset + data.items.length >= data.total} onClick={() => setOffset(offset + limit)}
                className="border border-[#262626] rounded-sm px-3 py-1.5 hover:bg-[#141414] disabled:opacity-40">Next</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function FilterInput({ label, testid, value, onChange }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">{label}</div>
      <input data-testid={testid} value={value} onChange={onChange} placeholder="filter…"
        className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none w-44" />
    </div>
  );
}

function EmptyState({ icon: Icon, msg, testid }) {
  return (
    <div className="text-center py-20 text-[#737373]" data-testid={testid}>
      <Icon size={38} className="mx-auto mb-3 opacity-50" />
      <p className="text-sm">{msg}</p>
    </div>
  );
}
